import { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyWallets, getMyRewards } from '../../api/walletApi';
import { getMyReferrals, getLevelUsers, getGenerationStatus, getMyRankHistory } from '../../api/userApi';
import { getMyFastrackNodes } from '../../api/fastrackApi';
import { getPublicConfig } from '../../api/authApi';

const accent = '#cc66ff';
const cyan = '#00e5ff';
const fmt = (v) => v != null ? Number(v).toFixed(2) : '—';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(153,51,255,0.55) 0%, rgba(20,0,40,0.9) 45%, #0d0d0d 70%)',
  color: '#fff',
  border: '1px solid #9933ff44',
};

const StatCard = ({ value, label, valueColor = accent, valueFontSize = 28, onClick, footerAction, xs = 12, md = 4 }) => (
  <Col xs={xs} md={md}>
    <Card style={{ ...cardDark, position: 'relative' }} className="p-3 h-100 dashboard-card">
      <Card.Body>
        <div style={{ fontSize: valueFontSize, fontWeight: 800, color: valueColor, marginBottom: 4 }}>
          {value}
        </div>
        <div style={{ color: '#aaa', fontSize: 13 }}>{label}</div>
        {footerAction && <div style={{ marginTop: 10 }}>{footerAction}</div>}
        {onClick && (
          <span
            onClick={onClick}
            style={{ position: 'absolute', bottom: 12, right: 16, color: cyan, fontSize: 22, fontWeight: 300, cursor: 'pointer', lineHeight: 1, zIndex: 2 }}
          >
            ›
          </span>
        )}
      </Card.Body>
    </Card>
  </Col>
);


const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cryptoDepositEnabled, setCryptoDepositEnabled] = useState(true);
  const [wallets, setWallets] = useState({ main_wallet: null, earning_wallet: null, one_wallet: null });
  const [referralCount, setReferralCount] = useState(null);
  const [levelUserCount, setLevelUserCount] = useState(null);
  const [fastrackBonus, setFastrackBonus] = useState(null);
  const [generationBonus, setGenerationBonus] = useState(null);
  const [performanceBonus, setPerformanceBonus] = useState(null);
  const [rewardBalance, setRewardBalance] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimeout = useRef(null);

  const referralLink = `https://app.onecircle.biz/register?ref=${user?.userCode || ''}`;

  const handleCopy = () => {
    const displayName = user?.username ? `@${user.username}` : user?.name;
    const message = `Hi,\n\n${displayName} has invited you to OneCircle Community\n\n"Build Your Circle,\nEarn Together"\n\nTo get registered with the OneCircle Community Globally, click on this link :\n\n${referralLink}`;
    navigator.clipboard.writeText(message).then(() => {
      setCopied(true);
      clearTimeout(copyTimeout.current);
      copyTimeout.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    getPublicConfig()
      .then((res) => {
        const enabled = res.data.config?.crypto_deposit_enabled;
        setCryptoDepositEnabled(enabled === undefined || enabled === 'true' || enabled === 'yes' || enabled === '1');
      })
      .catch(() => setCryptoDepositEnabled(true));
    getMyWallets()
      .then((res) => setWallets(res.data.wallets))
      .catch(() => setWallets({ main_wallet: { balance: 0 }, earning_wallet: { balance: 0 }, one_wallet: { balance: 0 } }));
    getMyReferrals()
      .then((res) => setReferralCount(res.data.count))
      .catch(() => setReferralCount(0));
    getLevelUsers()
      .then((res) => setLevelUserCount(res.data.count))
      .catch(() => setLevelUserCount(0));
    getMyFastrackNodes()
      .then((res) => setFastrackBonus(res.data.summary?.totalBonusEarned ?? 0))
      .catch(() => setFastrackBonus(0));
    getGenerationStatus()
      .then((res) => setGenerationBonus(res.data.totalEarned ?? 0))
      .catch(() => setGenerationBonus(0));
    getMyRankHistory()
      .then((res) => {
        const total = (res.data.payoutLogs || []).reduce((sum, p) => sum + (p.amountCredited || 0), 0);
        setPerformanceBonus(parseFloat(total.toFixed(2)));
      })
      .catch(() => setPerformanceBonus(0));
    getMyRewards()
      .then((res) => setRewardBalance(res.data.balance ?? 0))
      .catch(() => setRewardBalance(0));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">

        {/* Header */}
        <div className="mb-4">
          <h4 style={{ color: '#fff', fontWeight: 700 }}>Welcome, {user?.name}</h4>
          <div style={{ color: '#aaa', fontSize: 13 }}>Your OneCircle dashboard</div>
        </div>

        <Row className="g-3">

          {/* 1. Profile — full width mobile, 1/3 desktop */}
          <Col xs={12} md={4}>
            <Card style={{ ...cardDark, border: '1px solid #9933ff88' }} className="p-3 h-100 dashboard-card">
              <Card.Body>
                <div style={{ fontSize: 14, fontWeight: 700, color: accent, marginBottom: 12 }}>Profile</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Username</div>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{user?.username || user?.name || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>User Code</div>
                    <div style={{ color: cyan, fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{user?.userCode || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Total Earnings</div>
                    <div style={{ color: 'rgb(102, 255, 153)', fontSize: 13, fontWeight: 600 }}>
                      {fastrackBonus !== null && generationBonus !== null && performanceBonus !== null && rewardBalance !== null
                        ? `${fmt(fastrackBonus + generationBonus + performanceBonus + rewardBalance)} USDT`
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Slot Active</div>
                    <div style={{ color: 'rgb(0, 229, 255)', fontSize: 13, fontWeight: 600 }}>{user?.planId?.planName || '—'}</div>
                  </div>
                  {!user?.planId && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <Button
                        size="sm"
                        onClick={() => navigate('/slots')}
                        style={{ background: accent, border: 'none', color: '#000', fontWeight: 600, fontSize: 12 }}
                      >
                        Upgrade Slot
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* 2. Main Wallet — half mobile, 1/3 desktop */}
          <StatCard
            value={<>{fmt(wallets.main_wallet?.balance)} <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Main Wallet"
            valueColor="rgb(0, 229, 255)"
            valueFontSize={20}
            onClick={() => navigate('/wallet')}
            footerAction={cryptoDepositEnabled ? (
              <button
                onClick={(e) => { e.stopPropagation(); navigate('/wallet?addFunds=1'); }}
                style={{ background: 'rgba(102,255,153,0.15)', border: '1px solid rgba(102,255,153,0.3)', color: '#66ff99', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >
                + Add Funds
              </button>
            ) : undefined}
            xs={6} md={4}
          />

          {/* 3. One Wallet — half mobile, 1/3 desktop */}
          <StatCard
            value={<>{fmt(wallets.one_wallet?.balance)} <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="One Wallet"
            valueColor="rgb(0, 229, 255)"
            valueFontSize={20}
            onClick={() => navigate('/wallet?type=one')}
            xs={6} md={4}
          />

          {/* 4. Earning Wallet — full width mobile, 1/3 desktop */}
          <StatCard
            value={<>{fmt(wallets.earning_wallet?.balance)} <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Earning Wallet"
            valueColor="#66ff99"
            valueFontSize={20}
            onClick={() => navigate('/wallet?type=earning')}
            xs={12} md={4}
          />

          {/* 5. Direct Referrals — half mobile, 1/3 desktop */}
          <StatCard
            value={referralCount !== null ? referralCount : '—'}
            label="My Direct Referrals"
            valueColor="rgb(0, 229, 255)"
            valueFontSize={20}
            onClick={() => navigate('/referrals')}
            xs={6} md={4}
          />

          {/* 6. Level Users — half mobile, 1/3 desktop */}
          <StatCard
            value={levelUserCount !== null ? levelUserCount : '—'}
            label="Level Users"
            valueColor="rgb(0, 229, 255)"
            valueFontSize={20}
            onClick={() => navigate('/level-users')}
            xs={6} md={4}
          />

          {/* 7. Fastrack Bonus */}
          <StatCard
            value={<>{fmt(fastrackBonus)} <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Fastrack"
            valueColor="rgb(0, 229, 255)"
            valueFontSize={20}
            onClick={() => navigate('/fastrack')}
            xs={6} md={4}
          />

          {/* 8. Generation Bonus */}
          <StatCard
            value={<>{fmt(generationBonus)} <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Generation"
            valueColor="#66aaff"
            valueFontSize={20}
            onClick={() => navigate('/generation-records')}
            xs={6} md={4}
          />

          {/* 9. Performance Bonus */}
          <StatCard
            value={<>{fmt(performanceBonus)} <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Performance"
            valueColor="rgb(0, 229, 255)"
            valueFontSize={20}
            onClick={() => navigate('/performance-records')}
            xs={6} md={4}
          />

          {/* 10. Rewards */}
          <StatCard
            value={<>{fmt(rewardBalance)} <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Reward"
            valueColor="rgb(0, 229, 255)"
            valueFontSize={20}
            onClick={() => navigate('/rewards')}
            xs={6} md={4}
          />

          {/* 11. Account Status */}
          <Col xs={12} md={4}>
            <Card style={{ ...cardDark, cursor: 'pointer' }} className="p-3 h-100 dashboard-card" onClick={() => navigate('/account-status')}>
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: cyan, marginBottom: 4 }}>Account Status</div>
                  <div style={{ color: '#aaa', fontSize: 12 }}>View all your bonus & reward earnings</div>
                </div>
                <span style={{ color: cyan, fontSize: 26, fontWeight: 300 }}>›</span>
              </Card.Body>
            </Card>
          </Col>

          {/* 12. Referral Link — visible only after Slot 1 is purchased */}
          {user?.planId && <Col xs={12} md={12}>
            <Card style={{ ...cardDark, border: `1px solid ${cyan}44` }} className="p-3 dashboard-card">
              <Card.Body className="d-flex align-items-center justify-content-between" style={{ flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: cyan, marginBottom: 2 }}>Invite your friends & contacts to join your OneCircle community.</div>
                  <div style={{ color: '#888', fontSize: 12 }}>
                    Your code: <strong style={{ color: '#fff' }}>{user?.userCode || '—'}</strong>
                  </div>
                </div>
                <Button
                  onClick={handleCopy}
                  style={{
                    background: copied ? 'rgba(0,229,255,0.18)' : cyan,
                    border: `1px solid ${cyan}`,
                    color: copied ? cyan : '#000',
                    fontWeight: 700,
                    fontSize: 13,
                    padding: '8px 20px',
                    transition: 'all 0.2s',
                  }}
                >
                  {copied ? '✓ Copied!' : 'Copy Invite Link'}
                </Button>
              </Card.Body>
            </Card>
          </Col>}

        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;
